/**
 * useProductTransactions Hook
 * Fetches product_inventory_transactions with product, variant, and location joins.
 * Products use quantity_change (not quantity), variant_id, and from/to location fields.
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../../supabaseClient';

export default function useProductTransactions(companyId) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [timeFormat, setTimeFormat]     = useState('12h');

  // Fetch user time format preference
  useEffect(() => {
    const run = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data: profile } = await supabase
          .from('profiles')
          .select('time_format')
          .eq('id', user.id)
          .single();
        if (profile?.time_format) setTimeFormat(profile.time_format);
      } catch (_) {}
    };
    run();
  }, []);

  const updateTimeFormat = useCallback(async (newFormat) => {
    setTimeFormat(newFormat);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      await supabase.from('profiles').update({ time_format: newFormat }).eq('id', user.id);
    } catch (_) {}
  }, []);

  const fetchTransactions = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('product_inventory_transactions')
        .select(`
          *,
          products:product_id ( id, name, sku ),
          variants:variant_id ( id, name ),
          from_location:from_location_id ( id, name ),
          to_location:to_location_id ( id, name )
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      // Normalize: alias quantity_change → quantity so shared display helpers work
      const normalized = (data || []).map(tx => ({
        ...tx,
        quantity: tx.quantity_change
      }));

      setTransactions(normalized);
    } catch (err) {
      console.error('Error fetching product transactions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId) fetchTransactions();
  }, [companyId, fetchTransactions]);

  return {
    transactions,
    loading,
    error,
    refresh: fetchTransactions,
    timeFormat,
    updateTimeFormat
  };
}
